import React, { useEffect, useMemo, useState } from "react";
import { t } from "../i18n";
import Sidebar from "../components/Sidebar.jsx";
import { notifyGenerationComplete } from "../services/notifications";
import { fetchBillingStatus, fetchCreditEstimate } from "../services/billing.js";
import "./NanoStudio.css";

import { buildDownloadName, downloadFile } from "../utils/download";

export default function MusicStudio() {
    const storedState = useMemo(() => {
        try {
            const raw = window.sessionStorage.getItem("contentos_music_state");
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    }, []);

    const [loadingConfig, setLoadingConfig] = useState(true);
    const [configError, setConfigError] = useState("");
    const [models, setModels] = useState([]);
    const [scales, setScales] = useState([]);
    const [modes, setModes] = useState([]);

    const [modelId, setModelId] = useState(storedState.modelId || "");
    const [bpmEnabled, setBpmEnabled] = useState(Boolean(storedState.bpmEnabled));
    const [bpm, setBpm] = useState(storedState.bpm || 120);
    const [guidance, setGuidance] = useState(storedState.guidance ?? 4.0);
    const [temperature, setTemperature] = useState(storedState.temperature ?? 1.1);
    const [density, setDensity] = useState(storedState.density ?? 0.5);
    const [brightness, setBrightness] = useState(storedState.brightness ?? 0.5);
    const [scale, setScale] = useState(storedState.scale || null);
    const [mode, setMode] = useState(storedState.mode || "QUALITY");
    const [muteBass, setMuteBass] = useState(Boolean(storedState.muteBass));
    const [muteDrums, setMuteDrums] = useState(Boolean(storedState.muteDrums));
    const [onlyBassDrums, setOnlyBassDrums] = useState(Boolean(storedState.onlyBassDrums));

    const [promptText, setPromptText] = useState(storedState.promptText || "");
    const [promptWeight, setPromptWeight] = useState(storedState.promptWeight ?? 1.0);
    const [durationS, setDurationS] = useState(storedState.durationS || 12);
    const [extraPrompts, setExtraPrompts] = useState(storedState.extraPrompts || "");

    const [generating, setGenerating] = useState(false);
    const [resultUrl, setResultUrl] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [lastResultUrl, setLastResultUrl] = useState(storedState.lastResultUrl || "");
    const [activeProjectId, setActiveProjectId] = useState("");
    const [recentAudio, setRecentAudio] = useState([]);
    const [recentLoading, setRecentLoading] = useState(false);

    const [billingStatus, setBillingStatus] = useState(null);
    const [creditEstimate, setCreditEstimate] = useState(1);
    const [billingError, setBillingError] = useState("");

    useEffect(() => {
        let isMounted = true;
        fetchBillingStatus()
            .then((data) => {
                if (!isMounted) return;
                setBillingStatus(data);
            })
            .catch((err) => {
                if (!isMounted) return;
                setBillingError(err.message || "Erreur billing.");
            });
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        fetchCreditEstimate({
            gen_type: "audio",
            model_id: modelId,
            duration_s: durationS
        })
            .then((data) => {
                if (!isMounted) return;
                setCreditEstimate(Number(data.credits || 1));
            })
            .catch(() => {
                if (!isMounted) return;
                setCreditEstimate(1);
            });
        return () => {
            isMounted = false;
        };
    }, [modelId, durationS]);

    useEffect(() => {
        let isMounted = true;
        async function loadConfig() {
            setLoadingConfig(true);
            setConfigError("");
            try {
                const res = await fetch("/api/fast/audio/config");
                if (!res.ok) throw new Error("Impossible de charger la configuration.");
                const data = await res.json();
                if (!isMounted) return;
                setModels(data.models || []);
                setScales(data.scales || []);
                setModes(data.modes || []);
                if (!modelId && data.models?.length) setModelId(data.models[0].id);
            } catch (err) {
                if (isMounted) setConfigError(err.message || "Erreur inconnue.");
            } finally {
                if (isMounted) setLoadingConfig(false);
            }
        }
        loadConfig();
        return () => {
            isMounted = false;
        };
    }, [modelId]);

    useEffect(() => {
        try {
            const stored = window.sessionStorage.getItem("contentos_active_project_id");
            if (stored) setActiveProjectId(stored);
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        async function loadActiveProject() {
            try {
                const res = await fetch("/api/fast/user/settings");
                if (!res.ok) return;
                const data = await res.json();
                if (isMounted) setActiveProjectId(data.active_project_id || "");
            } catch {
                // ignore
            }
        }
        loadActiveProject();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        async function loadRecentAudio() {
            setRecentLoading(true);
            try {
                const params = new URLSearchParams();
                if (activeProjectId) params.set("project_id", activeProjectId);
                const query = params.toString();
                const res = await fetch(`/api/fast/media/recent/audio${query ? `?${query}` : ""}`);
                if (!res.ok) throw new Error("Impossible de charger les audios récents.");
                const data = await res.json();
                if (isMounted) setRecentAudio(Array.isArray(data.items) ? data.items : []);
            } catch {
                if (isMounted) setRecentAudio([]);
            } finally {
                if (isMounted) setRecentLoading(false);
            }
        }
        loadRecentAudio();
        return () => {
            isMounted = false;
        };
    }, [activeProjectId, lastResultUrl]);

    useEffect(() => {
        const payload = {
            modelId,
            bpmEnabled,
            bpm,
            guidance,
            temperature,
            density,
            brightness,
            scale,
            mode,
            muteBass,
            muteDrums,
            onlyBassDrums,
            promptText,
            promptWeight,
            durationS,
            extraPrompts,
            lastResultUrl
        };
        try {
            window.sessionStorage.setItem("contentos_music_state", JSON.stringify(payload));
        } catch {
            // Ignore storage errors.
        }
    }, [
        modelId,
        bpmEnabled,
        bpm,
        guidance,
        temperature,
        density,
        brightness,
        scale,
        mode,
        muteBass,
        muteDrums,
        onlyBassDrums,
        promptText,
        promptWeight,
        durationS,
        extraPrompts,
        lastResultUrl
    ]);

    async function handleGenerate() {
        if (!promptText.trim()) return;
        setGenerating(true);
        setErrorMsg("");
        try {
            const storedProjectId = (() => {
                try {
                    return window.sessionStorage.getItem("contentos_active_project_id") || "";
                } catch {
                    return "";
                }
            })();
            const projectId = activeProjectId || storedProjectId || null;
            const payload = {
                prompt: promptText.trim(),
                prompt_weight: promptWeight,
                extra_prompts: extraPrompts.trim() || null,
                duration_s: durationS,
                model_id: modelId,
                bpm: bpmEnabled ? bpm : null,
                guidance,
                temperature,
                density,
                brightness,
                scale: scale || null,
                mode,
                mute_bass: muteBass,
                mute_drums: muteDrums,
                only_bass_and_drums: onlyBassDrums,
                project_id: projectId
            };
            const res = await fetch("/api/fast/audio/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error(data.error || "Génération impossible.");
            setResultUrl(data.url || "");
            setLastResultUrl(data.url || "");
            // Notification de succès
            notifyGenerationComplete("audio", true, {
                message: "Votre musique a été générée avec succès !",
                url: "/music"
            });
        } catch (err) {
            setErrorMsg(err.message || "Erreur génération.");
            // Notification d'échec
            notifyGenerationComplete("audio", false, {
                error: err.message || "Erreur lors de la génération"
            });
        } finally {
            setGenerating(false);
        }
    }

    const creditsRemaining = billingStatus?.credits_remaining;
    const creditsNeeded = Number.isFinite(creditEstimate) ? creditEstimate : 1;
    const hasCredits = typeof creditsRemaining === "number" ? creditsRemaining >= creditsNeeded : true;
    const missingCredits =
        typeof creditsRemaining === "number" ? Math.max(creditsNeeded - creditsRemaining, 0) : 0;

    return (
        <div className="app-shell">
            <Sidebar active="music" />
            <div className="app-main">
                <div className="nano-page">
                    <header className="nano-header">
                        <div>
                            <p className="nano-kicker">{t("music.kicker")}</p>
                            <h1 className="nano-title page-title-accent">{t("music.title")}</h1>
                            <p className="nano-subtitle">{t("music.subtitle")}</p>
                        </div>
                    </header>

                    {configError && <div className="nano-alert error">{configError}</div>}
                    {errorMsg && <div className="nano-alert error">{errorMsg}</div>}

                    <div className="nano-layout">
                        <div className="nano-columns">
                            <section className="nano-panel">
                                <h2>{t("music.config_title")}</h2>
                                {loadingConfig ? (
                                    <p>{t("common.loading")}</p>
                                ) : (
                                    <>
                                        <div className="nano-config-row">
                                            <label>
                                                {t("music.model")}
                                                <select value={modelId} onChange={(e) => setModelId(e.target.value)}>
                                                    {models.map((model) => (
                                                        <option key={model.id} value={model.id}>
                                                            {model.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>

                                            <label className="nano-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={bpmEnabled}
                                                    onChange={(e) => setBpmEnabled(e.target.checked)}
                                                />
                                                {t("music.bpm_enable")}
                                            </label>

                                            {bpmEnabled && (
                                                <label className="nano-range">
                                                    {t("music.bpm")} <span>{bpm}</span>
                                                    <input
                                                        type="range"
                                                        min="60"
                                                        max="200"
                                                        value={bpm}
                                                        onChange={(e) => setBpm(Number(e.target.value))}
                                                    />
                                                </label>
                                            )}

                                            <label className="nano-range">
                                                {t("music.guidance")} <span>{guidance.toFixed(1)}</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="6"
                                                    step="0.1"
                                                    value={guidance}
                                                    onChange={(e) => setGuidance(Number(e.target.value))}
                                                />
                                            </label>

                                            <label className="nano-range">
                                                {t("music.temperature")} <span>{temperature.toFixed(1)}</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="3"
                                                    step="0.1"
                                                    value={temperature}
                                                    onChange={(e) => setTemperature(Number(e.target.value))}
                                                />
                                            </label>

                                            <label className="nano-range">
                                                {t("music.density")} <span>{density.toFixed(2)}</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.05"
                                                    value={density}
                                                    onChange={(e) => setDensity(Number(e.target.value))}
                                                />
                                            </label>

                                            <label className="nano-range">
                                                {t("music.brightness")} <span>{brightness.toFixed(2)}</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.05"
                                                    value={brightness}
                                                    onChange={(e) => setBrightness(Number(e.target.value))}
                                                />
                                            </label>

                                            <label>
                                                {t("music.scale")}
                                                <select value={scale || ""} onChange={(e) => setScale(e.target.value || null)}>
                                                    {scales.map((item) => (
                                                        <option key={item.value || "auto"} value={item.value || ""}>
                                                            {item.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>

                                            <label>
                                                {t("music.mode")}
                                                <select value={mode} onChange={(e) => setMode(e.target.value)}>
                                                    {modes.map((item) => (
                                                        <option key={item.value} value={item.value}>
                                                            {item.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>

                                            <label className="nano-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={muteBass}
                                                    onChange={(e) => setMuteBass(e.target.checked)}
                                                />
                                                {t("music.mute_bass")}
                                            </label>

                                            <label className="nano-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={muteDrums}
                                                    onChange={(e) => setMuteDrums(e.target.checked)}
                                                />
                                                {t("music.mute_drums")}
                                            </label>

                                            <label className="nano-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={onlyBassDrums}
                                                    onChange={(e) => setOnlyBassDrums(e.target.checked)}
                                                />
                                                {t("music.only_bass_drums")}
                                            </label>
                                        </div>
                                    </>
                                )}
                            </section>

                            <section className="nano-panel">
                                <h2>{t("music.prompt_label")}</h2>
                                <textarea
                                    value={promptText}
                                    onChange={(e) => setPromptText(e.target.value)}
                                    rows={6}
                                    placeholder={t("music.prompt_placeholder")}
                                />

                                <label className="nano-range">
                                    {t("music.weight")} <span>{promptWeight.toFixed(1)}</span>
                                    <input
                                        type="range"
                                        min="0.1"
                                        max="3"
                                        step="0.1"
                                        value={promptWeight}
                                        onChange={(e) => setPromptWeight(Number(e.target.value))}
                                    />
                                </label>

                                <label className="nano-range">
                                    {t("music.duration")} <span>{durationS}s</span>
                                    <input
                                        type="range"
                                        min="5"
                                        max="30"
                                        value={durationS}
                                        onChange={(e) => setDurationS(Number(e.target.value))}
                                    />
                                </label>

                                <label>
                                    {t("music.extra_prompts")}
                                    <textarea
                                        value={extraPrompts}
                                        onChange={(e) => setExtraPrompts(e.target.value)}
                                        rows={4}
                                        placeholder={t("music.extra_prompts_help")}
                                    />
                                </label>

                                <div className="nano-prompt-actions">
                                    <button
                                        className="nano-primary"
                                        disabled={generating || !promptText.trim() || !hasCredits}
                                        onClick={handleGenerate}
                                    >
                                        {generating ? t("music.spinner") : t("music.generate")}
                                        <span className="credit-badge">{creditsNeeded}</span>
                                    </button>
                                </div>

                                {billingError && <div className="nano-alert error">{billingError}</div>}
                                {!hasCredits && missingCredits > 0 && (
                                    <button
                                        type="button"
                                        className="billing-upgrade-link"
                                        onClick={() => (window.location.href = "/pricing")}
                                    >
                                        {missingCredits} / {creditsNeeded} crédits manquants, rechargez !
                                    </button>
                                )}
                            </section>
                        </div>

                        <section className="nano-panel">
                            <h2>{t("music.recent_title")}</h2>
                            {recentLoading && <p>{t("common.loading")}</p>}
                            {!recentLoading && recentAudio.length === 0 && <p>Aucun audio récent.</p>}
                            {!recentLoading && recentAudio.length > 0 && (
                                <div className="music-recent-grid">
                                    {recentAudio.map((item, index) => {
                                        const audioUrl = item?.asset?.url || "";
                                        const promptText = item?.generation?.prompt_text || "Sans titre";
                                        return (
                                            <div key={`${item?.generation?.generation_id || "audio"}-${index}`} className="music-recent-item">
                                                <div className="music-recent-info">
                                                    <p className="music-recent-prompt">{promptText}</p>
                                                    {audioUrl && (
                                                        <>
                                                            <audio controls src={audioUrl} />
                                                            <button
                                                                type="button"
                                                                className="nano-secondary nano-download"
                                                                onClick={() => downloadFile(audioUrl, buildDownloadName(audioUrl))}
                                                            >
                                                                <span className="material-symbols-rounded">download</span>
                                                                Télécharger
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>

                        <section className="nano-panel">
                            <div className="nano-panel-header">
                                <h2>{t("music.latest")}</h2>
                                {resultUrl && (
                                    <button
                                        type="button"
                                        className="nano-secondary nano-download"
                                        onClick={() => downloadFile(resultUrl, buildDownloadName(resultUrl))}
                                    >
                                        <span className="material-symbols-rounded">download</span>
                                        Télécharger
                                    </button>
                                )}
                            </div>
                            {resultUrl ? (
                                <div className="nano-result">
                                    <audio controls src={resultUrl} />
                                    <a className="nano-link" href={resultUrl} target="_blank" rel="noreferrer">
                                        {t("music.open_audio")}
                                    </a>
                                </div>
                            ) : (
                                <p>{t("music.result_empty")}</p>
                            )}
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}
